import React, { useEffect, useRef } from 'react';

import { useMultiMacroQueryStoreHook } from '@confluence/fabric-extension-queries/entry-points/multiMacroQuery.ts';
import { queryStaticMacro } from '@confluence/fabric-extension-queries/entry-points/singleStaticMacroQuery.ts';
import { fg } from '@confluence/feature-gating';
import { getLogger } from '@confluence/logger';

import { INSERTION_REFETCH_GIVE_UP_DELAY_MS } from './ForgeMacro.tsx';
import {
	StaticMacroStoreProvider,
	type StaticMacroAttempt,
	useStaticMacroStoreContext,
} from './StaticMacroStoreContext.tsx';

const logger = getLogger('StaticMacroDataBridge');

const INSERTION_RETRY_INITIAL_DELAY_MS = 1000;
const INSERTION_RETRY_MAX_DELAY_MS = 8000;
const INSERTION_RETRY_MAX_JITTER_PERCENT = 10;

function getInsertionRetryDelay(retryAttempt: number): number {
	const baseDelay = Math.min(
		INSERTION_RETRY_INITIAL_DELAY_MS * 2 ** retryAttempt,
		INSERTION_RETRY_MAX_DELAY_MS,
	);
	const maxJitter = baseDelay * (INSERTION_RETRY_MAX_JITTER_PERCENT / 100);
	return Math.round(baseDelay + Math.random() * maxJitter);
}

/**
 * This loads the multi-macro query store into the static macro store context.
 *
 * Sits in `MultiMacroQueryContainer` and `StaticMacroStoreProvider`, reads `staticMacroData` from the query store
 * and registers entries into the static macro context. Each `ForgeMacro` is only notified when its own data arrives.
 * When all staticMacros pages have been fetched, calls `markFullyLoaded` so pending macros know to fall back to the iframe renderer.
 *
 * Also wires up `ctx.triggerRefetch` (via `setRefetchTrigger`) to the single-macro
 * `confluence_staticMacro` query, so an individual `ForgeMacro` instance can request fresh static
 * HTML for itself without re-fetching/re-paginating every macro on the page.
 */
export function StaticMacroDataBridge({
	draftSyncCompletedRef,
	expectedContentId,
}: {
	draftSyncCompletedRef?: React.MutableRefObject<(() => void) | null>;
	expectedContentId?: string;
}): null {
	const ctx = useStaticMacroStoreContext();
	const [{ staticMacroData, staticMacrosComplete, contentId, cloudId, mode }] =
		useMultiMacroQueryStoreHook();

	// List of all currently registered localIds so we don't do duplicate work if we received a localId multiple times.
	const registeredLocalIds = useRef<Set<string>>(new Set());
	const prevStaticMacroPagesRef = useRef<Record<string, any> | null>(null);
	const fullyLoadedRef = useRef(false);
	const registeredForContentIdRef = useRef(expectedContentId);

	useEffect(() => {
		if (!ctx || !draftSyncCompletedRef) {
			return undefined;
		}
		draftSyncCompletedRef.current = ctx.notifyDraftSyncCompleted;
		return () => {
			draftSyncCompletedRef.current = null;
		};
	}, [ctx, draftSyncCompletedRef]);

	useEffect(() => {
		if (!ctx || !contentId) {
			return undefined;
		}

		ctx.setRefetchTrigger((localId: string, attempt: StaticMacroAttempt) => {
			const scheduleInsertionRetry = (retryAttempt: number) => {
				const startedAt = ctx.storeRef.current?.insertionRefetchStartedAt.get(localId);
				if (startedAt === undefined) return false;

				const delay = getInsertionRetryDelay(retryAttempt);
				const deadline = startedAt + INSERTION_REFETCH_GIVE_UP_DELAY_MS;
				if (Date.now() + delay >= deadline) {
					return false;
				}

				setTimeout(() => fetchStaticMacro(retryAttempt + 1), delay);
				return true;
			};

			const fetchStaticMacro = (retryAttempt: number) => {
				void queryStaticMacro({ contentId, cloudId, localId, mode })
					.then((result) => {
						if (!result) {
							if (attempt === 'insertion') {
								scheduleInsertionRetry(retryAttempt);
							}
							return;
						}
						if (result.value) {
							ctx.register(localId, result.value, { force: true, attempt });
						} else if (result.errorCode) {
							ctx.registerError(localId, result.errorCode, { force: true, attempt });
						}
					})
					.catch((error) => {
						if (attempt === 'insertion' && scheduleInsertionRetry(retryAttempt)) {
							return;
						}
						if (attempt !== 'insertion' && retryAttempt === 0) {
							fetchStaticMacro(1);
							return;
						}
						logger.error`Static macro refetch failed after retry for localId ${localId} on content ${contentId}: ${error}`;
						ctx.registerError(localId, 'REFETCH_FAILED', { force: true, attempt });
					});
			};

			fetchStaticMacro(0);
		});

		return () => ctx.setRefetchTrigger(null);
	}, [ctx, contentId, cloudId, mode]);

	useEffect(() => {
		if (!ctx) return;

		if (expectedContentId !== undefined && fg('conf_frontend_static_macro_perf')) {
			if (registeredForContentIdRef.current !== expectedContentId) {
				registeredForContentIdRef.current = expectedContentId;
				registeredLocalIds.current = new Set();
				prevStaticMacroPagesRef.current = null;
				fullyLoadedRef.current = false;
			}
			// The query store still holds the previous page's results, including `staticMacrosComplete`.
			if (contentId !== expectedContentId) return;
		}

		if (staticMacroData !== prevStaticMacroPagesRef.current) {
			prevStaticMacroPagesRef.current = staticMacroData;

			for (const node of Object.values(staticMacroData ?? {})) {
				const { localId, value, error } = node;
				if (!localId || registeredLocalIds.current.has(localId)) continue;
				registeredLocalIds.current.add(localId);

				if (value) {
					ctx.register(localId, value);
				} else if (error) {
					ctx.registerError(localId, error.code);
				}
			}
		}

		if (staticMacrosComplete && !fullyLoadedRef.current) {
			fullyLoadedRef.current = true;
			ctx.markFullyLoaded();
		}
	}, [ctx, staticMacroData, staticMacrosComplete, contentId, expectedContentId]);

	return null;
}

export function StaticMacrosBridge({
	children = null,
	draftSyncCompletedRef,
	contentId,
}: {
	children?: React.ReactNode;
	draftSyncCompletedRef?: React.MutableRefObject<(() => void) | null>;
	/** Pass on surfaces that navigate between content without remounting, such as the view page. */
	contentId?: string;
}): React.ReactNode {
	// Always render the provider so the tree shape is stable between SSR and the first client render.
	return (
		<StaticMacroStoreProvider contentId={contentId}>
			{fg('conf_frontend_forge_static_macro_mvp') && (
				<StaticMacroDataBridge
					draftSyncCompletedRef={draftSyncCompletedRef}
					expectedContentId={contentId}
				/>
			)}
			{children}
		</StaticMacroStoreProvider>
	);
}
